<template>
  <div class="relative">
    <div class="flex justify-end">
      <Dropdown
        v-model="selectedCurrency"
        class="mb-5 w-full md:w-auto bg-card border-0 md:bg-transparent md:border"
        :options="['Default', 'USD', 'CNY']"
        @change="getData"
      />
    </div>

    <DailyIndicatorSection v-if="isAuthenticated() && isLithium" class="mb-5" />

    <div v-if="loading" class="mt-20">
      <TheLoader />
    </div>

    <template v-else>
      <div class="rounded-3xl bg-white pt-9 shadow-pm">
        <div class="relative overflow-x-auto">
          <div class="min-w-[1050px]">
            <div
              class="mx-6 -mb-3.5 rounded-lg bg-s-300 px-3 py-2 text-lg font-semibold leading-[22px]"
            >
              {{ product.name }}
            </div>
            <SummaryTableNewPortal
              :price-summary="priceSummary"
              :category="category"
              :subscription="subscription"
              :initial-fields="initialFields"
            />
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'

import SummaryTableNewPortal from '~/components/price-assessments/SummaryTableNewPortal.vue'
import TheLoader from '~/components/TheLoader.vue'
import DailyIndicatorSection from '~/components/portals/DailyIndicator/index.vue'
import Dropdown from '~/components/shared/common/Dropdown.vue'

import type { TProduct } from '~/src/types/product'
import { useAuthentication } from '~/composables/useAuthentication'
import { useGetData } from '~/composables/useGetData'
import { logError } from '~/utils/log-error'
import {
  LITHIUM,
  PRICE_ASSESSMENTS,
  DAILY_PRICE_INDICATOR,
} from '~/constants/services/constants'

const props = defineProps<{
  product: TProduct
  subscription: string
}>()

const getData = async () => {
  try {
    loading.value = true

    await getSummaryData(
      selectedCurrency.value !== 'Default' ? selectedCurrency.value : undefined,
    )

    priceSummary.value = pricesSummaryPortal.value
      .map((item) => ({
        ...item,
        values: item.values.filter(
          (value) => value.priceType.name !== DAILY_PRICE_INDICATOR,
        ),
      }))
      .filter((item) => item.values.length)
  } catch (error) {
    logError(error)
  } finally {
    loading.value = false
  }
}

const { isAuthenticated } = useAuthentication(getData)
const category = ref(props.product.category)
const initialFields = ref([props.product.slug, PRICE_ASSESSMENTS.slug])
const loading = ref(true)
const priceSummary = ref([])
const isLithium = computed(() => props.product.slug === LITHIUM.slug)
const selectedCurrency = ref('Default')

const { getSummaryData, pricesSummaryPortal } = useGetData(category.value)

onMounted(() => {
  getData()
})
</script>
